import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { TPlayerColorVariant } from '../../games/player-color-variant';
import { usePrefersReducedMotion } from '../../../hooks/usePrefersReducedMotion';
import {
  getTurnStampVisibleDurationMs,
  resolveTurnStampColorVariant,
  type ITurnStampGameUpdate,
  type TTurnStampTextKey,
} from '../utils/turn-stamp';

interface IUseTurnStampParams {
  activeLobbyId: string | null;
  gameSlug: string;
  gameUpdate: ITurnStampGameUpdate | null;
  initialPlayerSlot?: 0 | 1;
  isMyTurn: boolean;
}

export interface ITurnStampState {
  isVisible: boolean;
  textKey: TTurnStampTextKey;
  colorVariant: TPlayerColorVariant;
  prefersReducedMotion: boolean;
}

export function useTurnStamp({
  activeLobbyId,
  gameSlug,
  gameUpdate,
  initialPlayerSlot,
  isMyTurn,
}: IUseTurnStampParams): ITurnStampState {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isVisible, setIsVisible] = useState(false);
  const [textKey, setTextKey] = useState<TTurnStampTextKey>(
    'game.turnStamp.yourTurn',
  );
  const hideTimeoutRef = useRef<number | null>(null);
  const initializedStampLobbyIdRef = useRef<string | null>(null);
  const didShowInitialStampRef = useRef(false);

  const hideStamp = useCallback(() => {
    setIsVisible(false);
    if (hideTimeoutRef.current !== null) {
      window.clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const showStamp = useCallback(
    (nextTextKey: TTurnStampTextKey) => {
      setTextKey(nextTextKey);
      setIsVisible(true);
      if (hideTimeoutRef.current !== null) {
        window.clearTimeout(hideTimeoutRef.current);
      }
      hideTimeoutRef.current = window.setTimeout(() => {
        setIsVisible(false);
        hideTimeoutRef.current = null;
      }, getTurnStampVisibleDurationMs(prefersReducedMotion));
    },
    [prefersReducedMotion],
  );

  useEffect(() => {
    if (activeLobbyId === null) {
      initializedStampLobbyIdRef.current = null;
      didShowInitialStampRef.current = false;
      hideStamp();
      return;
    }

    if (initializedStampLobbyIdRef.current !== activeLobbyId) {
      initializedStampLobbyIdRef.current = activeLobbyId;
      didShowInitialStampRef.current = false;
      hideStamp();
    }
  }, [activeLobbyId, hideStamp]);

  useEffect(() => {
    if (
      activeLobbyId === null ||
      !gameUpdate ||
      didShowInitialStampRef.current
    ) {
      return;
    }

    didShowInitialStampRef.current = true;
    showStamp(
      isMyTurn ? 'game.turnStamp.yourTurn' : 'game.turnStamp.opponentTurn',
    );
  }, [activeLobbyId, gameUpdate, isMyTurn, showStamp]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current !== null) {
        window.clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const colorVariant = useMemo(
    () =>
      resolveTurnStampColorVariant(
        gameSlug,
        gameUpdate,
        initialPlayerSlot,
        textKey === 'game.turnStamp.yourTurn',
      ),
    [gameSlug, gameUpdate, initialPlayerSlot, textKey],
  );

  return {
    isVisible,
    textKey,
    colorVariant,
    prefersReducedMotion,
  };
}
